import useModal from '../../hooks/useModal';
import { useSetupSignlessTrading } from 'src/hooks/wallets/privy/useSetupSignlessTrading';
import { useCallback, useState } from 'react';
import { EnableSignlessTradingStepCard } from './EnableSignlessTradingStepCard';
import ResponsiveModal from '../../ResponsiveModal/ResponsiveModal';
import Button from 'src/components/primitives/Button/Button';
import Checkbox from 'src/components/primitives/Checkbox/Checkbox';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import { useDebounce } from 'react-use';

const VARIANT_COPY = {
	trading: {
		title: 'Enable Trading',
		description:
			'Two signatures are required to verify your ownership and enable Auto-Signing while trading on Velocity.',
		step1Title: 'Establish Connection',
		step1Description:
			'Sign this message to verify ownership of your Velocity account',
		step2Title: 'Enable Auto-Signing',
		step2Description:
			'Sign this transaction to enable gasless and signless trading on Velocity',
	},
	address_book: {
		title: 'Authenticate',
		description:
			'Two signatures are required to verify your ownership and enable the Address Book on Velocity for withdrawal.',
		step1Title: 'Establish Connection',
		step1Description:
			'Sign this message to verify ownership of your Velocity account',
		step2Title: 'Enable Address Book',
		step2Description:
			'Sign this message to view and edit your Address Book on Velocity',
	},
} as const;

export default function EnableSignlessTradingModal({
	variant = 'trading',
}: {
	variant?: 'trading' | 'address_book';
}) {
	const { hide } = useModal();
	const {
		connectToPrivy,
		setupDelegates,
		connectingToPrivyState,
		settingUpDelegatesState,
	} = useSetupSignlessTrading();

	const copy = VARIANT_COPY[variant];
	const [isLedgerWallet, setIsLedgerWallet] = useState(false);

	const isLedgerRef = useSyncedRef(isLedgerWallet);

	useDebounce(
		() => {
			setupDelegates(isLedgerRef.current).then((result) => {
				if (result) {
					hide();
				}
			});
		},
		500, // we debounce to prevent multiple calls to setupDelegates
		[setupDelegates, hide]
	);

	const handleSignInPrivy = useCallback(() => {
		connectToPrivy(isLedgerWallet);
	}, [connectToPrivy, isLedgerWallet]);

	return (
		<ResponsiveModal.Container
			onClose={hide}
			dataTestId="enable-signless-trading-modal"
		>
			<ResponsiveModal.Title closeButtonTestId="enable-signless-trading-modal-close">
				{copy.title}
			</ResponsiveModal.Title>
			<ResponsiveModal.Body>
				<p>{copy.description}</p>

				<EnableSignlessTradingStepCard
					title={copy.step1Title}
					description={copy.step1Description}
					state={connectingToPrivyState}
				/>
				<EnableSignlessTradingStepCard
					title={copy.step2Title}
					description={copy.step2Description}
					state={settingUpDelegatesState}
				/>
			</ResponsiveModal.Body>

			<ResponsiveModal.Footer className="flex-col items-stretch">
				<Checkbox.Default
					id="ledger-wallet-checkbox"
					checked={isLedgerWallet}
					onCheckedChange={() => setIsLedgerWallet((prev) => !prev)}
					label="I'm using a Ledger hardware wallet"
				/>

				<Button
					size="lg"
					onClick={handleSignInPrivy}
					dataTestId="enable-signless-trading-modal-submit"
				>
					Confirm
				</Button>
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
}
